import { useState, type ReactNode } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { clsx, FullscreenDialog } from '../../components/ui';
import { useIsMobile } from '../../lib/hooks';
import { ui } from '../../store/ui';

export interface SettingsSection {
  id: string;
  label: string;
  group?: string;
  danger?: boolean;
  onSelect?: () => void;
  render?: () => ReactNode;
}

export function SettingsLayout({ title, sections, initial }: { title: string; sections: SettingsSection[]; initial?: string }) {
  const isMobile = useIsMobile();
  const [active, setActive] = useState<string | null>(() => (initial && sections.some((s) => s.id === initial) ? initial : isMobile ? null : (sections.find((s) => s.render)?.id ?? null)));
  const current = sections.find((s) => s.id === active);
  const groups = [...new Set(sections.map((s) => s.group ?? ''))];

  const nav = (
    <nav aria-label={`${title} sections`} className="scroll-thin flex w-full flex-col overflow-y-auto py-10 pl-4 pr-2 md:w-64 md:items-stretch">
      {groups.map((g) => (
        <div key={g} className="mb-3">
          {g && <div className="px-2.5 pb-1 text-xs font-semibold uppercase tracking-wide text-faint">{g}</div>}
          {sections
            .filter((s) => (s.group ?? '') === g)
            .map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => (s.onSelect ? s.onSelect() : setActive(s.id))}
                aria-current={active === s.id ? 'page' : undefined}
                className={clsx('block w-full rounded-md px-2.5 py-1.5 text-left text-[15px]', s.danger ? 'text-danger hover:bg-danger-soft' : active === s.id ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg')}
              >
                {s.label}
              </button>
            ))}
        </div>
      ))}
    </nav>
  );

  return (
    <FullscreenDialog open onClose={ui.close} title={title}>
      <div className="flex size-full">
        {(!isMobile || !current) && (
          <div className={clsx('flex justify-end bg-sidebar', isMobile ? 'w-full' : 'w-[34%] min-w-64')}>
            <div className="w-full md:w-64">
              <div className="px-6 pt-6 text-sm font-semibold text-fg md:hidden">{title}</div>
              {nav}
            </div>
          </div>
        )}
        {current && (
          <div className="relative flex min-w-0 flex-1">
            <div className="scroll-thin w-full max-w-3xl overflow-y-auto px-5 py-10 sm:px-10">
              {isMobile && (
                <button type="button" onClick={() => setActive(null)} className="mb-4 flex items-center gap-1 text-sm text-muted hover:text-fg">
                  <ArrowLeft className="size-4" /> {title}
                </button>
              )}
              <h1 className="mb-6 text-xl font-bold text-fg">{current.label}</h1>
              {current.render?.()}
            </div>
          </div>
        )}
        <button type="button" onClick={ui.close} aria-label="Close settings" className="absolute right-4 top-4 flex size-9 items-center justify-center rounded-full border-2 border-line-strong text-muted hover:text-fg">
          <X className="size-5" />
        </button>
      </div>
    </FullscreenDialog>
  );
}

export function Section({ title, description, children }: { title?: string; description?: string; children: ReactNode }) {
  return (
    <section className="mb-8 border-b border-line pb-8 last:border-0">
      {title && <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">{title}</h2>}
      {description && <p className="mt-1 text-sm text-faint">{description}</p>}
      <div className={clsx(title && 'mt-4', 'space-y-3')}>{children}</div>
    </section>
  );
}
